import styled from '@emotion/styled';
import type {PlayerResizedEvent, VTTProperties} from 'bitmovin-player';
import {FC, useState} from 'react';
import {usePlayerCue} from '../../hooks/usePlayerCue';
import {usePlayerResize} from '../../hooks/usePlayerResize';
import {calculateContainerId, calculateCueId} from '../../utils/vttUtils';
import {Region} from './region';
import {Cue} from './cue';

export type Cue = {
  id: string;
  text: string;
  containerId: string;
  vtt?: VTTProperties;
};

export const SubtitleContainer: FC = () => {
  const [fontSize, setFontSize] = useState('1em');
  const [cues, setCues] = useState<Cue[]>([]);

  usePlayerCue({
    onCueEnter: (event) => {
      // Sanitize cue data
      if (event.position) {
        // Sometimes the positions are undefined, we assume them to be zero
        event.position.row = event.position.row || 0;
        event.position.column = event.position.column || 0;
      }

      const id = calculateCueId(event);
      const containerId = calculateContainerId(event);

      setCues((prevState) => [
        ...prevState,
        {
          id,
          text: event.text,
          containerId,
          vtt: event.vtt || undefined,
        },
      ]);
    },
    onCueExit: (event) => {
      const id = calculateCueId(event);

      /* Take a FIFO approach to multiple cues with the same ID. When there are multiple cues with
       * the same id, there is no way to know which of the cues is the one to be deleted, so we just
       * hope that FIFO works fine. Theoretically there is a case where two colliding ids are
       * removed at different times, in the wrong order. According to the Bitmovin team, this rare
       * case has yet to be observed.
       */
      setCues((prevState) => {
        const indexOf = prevState.findIndex((el) => el.id === id);
        const temp = [...prevState];
        temp.splice(indexOf, 1);
        return temp;
      });
    },
  });

  usePlayerResize((e: PlayerResizedEvent) => {
    const santizedWidth = Number(e.width.replace(/[^0-9]/g, ''));
    setFontSize(
      `${Math.max(Number((santizedWidth / 1000).toFixed(2)), 1.2)}em`
    );
  });

  return (
    <Container>
      {cues.map((cue) => (
        <Region vtt={cue.vtt} key={cue.id}>
          <Cue key={cue.id} cue={cue} fontSize={fontSize} />
        </Region>
      ))}
    </Container>
  );
};

const Container = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  // Height of the control bar. This is a temp value until UI hiding is implemented.
  bottom: 8.875em;
  left: 0;
  padding: 1.5em;
  pointer-events: none;
`;
